// Utility functions for reporting components

export function getStatusColor(status: string): string {
  // Normalize status: convert underscores to hyphens and lowercase
  const normalized = status.toLowerCase().replace(/_/g, '-');
  
  switch (normalized) {
    case 'deployed':
    case 'completed':
    case 'vending-complete':
    case 'account-vending-complete':
    case 'customization-complete':
      return 'success';
    case 'deploying':
    case 'in-progress':
    case 'running':
    case 'account-vending':
    case 'account-vending-initiated':
    case 'account-created':
    case 'customization-in-progress':
      return 'info';
    case 'pending':
    case 'queued':
    case 'pending-approval':
    case 'pending-customization-approval':
      return 'warning';
    case 'failed':
    case 'error':
    case 'customization-failed':
      return 'danger';
    default:
      return 'secondary';
  }
}

export function getStatusIcon(status: string): string {
  // Normalize status: convert underscores to hyphens and lowercase
  const normalized = status.toLowerCase().replace(/_/g, '-');
  
  switch (normalized) {
    case 'deployed':
    case 'completed':
    case 'vending-complete':
    case 'account-vending-complete':
    case 'customization-complete':
      return '✅';
    case 'deploying':
    case 'in-progress':
    case 'running':
    case 'customization-in-progress':
      return '🔄';
    case 'account-vending':
    case 'account-vending-initiated':
      return '🏗️';
    case 'account-created':
      return '🎉';
    case 'pending':
    case 'queued':
    case 'pending-approval':
    case 'pending-customization-approval':
      return '⏳';
    case 'failed':
    case 'error':
    case 'customization-failed':
      return '❌';
    default:
      return '⚪';
  }
}

export function formatTimestamp(timestamp?: string): string {
  if (!timestamp) return '-';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}

export function formatFullTimestamp(timestamp?: string): string {
  if (!timestamp) return '-';
  
  const date = new Date(timestamp);
  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function getStatusText(status: string): string {
  // Normalize status: convert underscores to hyphens and lowercase
  const normalized = status.toLowerCase().replace(/_/g, '-');
  
  switch (normalized) {
    case 'deployed':
    case 'customization-complete':
      return 'Deployed';
    case 'completed':
      return 'Completed';
    case 'vending-complete':
    case 'account-vending-complete':
      return 'Vending Complete';
    case 'deploying':
    case 'customization-in-progress':
      return 'Deploying';
    case 'in-progress':
      return 'In Progress';
    case 'running':
      return 'Running';
    case 'account-vending':
    case 'account-vending-initiated':
      return 'Vending Account';
    case 'account-created':
      return 'Account Created';
    case 'failed':
    case 'customization-failed':
      return 'Failed';
    case 'error':
      return 'Error';
    case 'pending':
      return 'Pending';
    case 'queued':
      return 'Queued';
    case 'pending-approval':
    case 'pending-customization-approval':
      return 'Pending Approval';
    default:
      // Fallback: capitalize and replace separators with spaces
      return normalized
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
  }
}
